import type { ReactNode } from 'react';
import React from 'react';
import { useAppDispatch } from '../../grid/reduxStore/Store';
import { setLastContextMenuPosition, setCanPaste } from 'components/editor/grid/reduxStore/contextMenuSlice';
import { useSectionId } from 'components/editor/Sections/SectionIdProvider';
import { readBlockFromClipboard } from 'components/editor/utils/clipboard';
import CustomContextMenu from './CustomContextMenu.tsx';

export type MenuItemType =
  | {
      type: 'item';
      actionIcon?: React.ReactNode;
      title: string;
      callback: () => void;
      disabled?: boolean;
      icon?: React.ReactNode;
    }
  | {
      type: 'divider';
    }
  | {
      type: 'submenu';
      actionIcon?: React.ReactNode;
      title: string;
      icon?: React.ReactNode;
      children: MenuItemType[];
    };

type CustomContextMenuProps = {
  children: ReactNode;
  options: CustomContextMenuOptions;
  testId: string;
  isEnabled?: boolean;
};

export type ContextMenuEvent = React.MouseEvent<HTMLDivElement> & {
  target: HTMLElement;
};

export type CustomContextMenuOptions = MenuItemType[];

export const CustomContextMenuContainer = ({ options, testId, children, isEnabled = true }: CustomContextMenuProps) => {
  const [contextMenu, setContextMenu] = React.useState<{
    mouseXAxis: number;
    mouseYAxis: number;
  } | null>(null);
  const dispatch = useAppDispatch();
  const sectionId = useSectionId();

  const handleContextMenu = async (event: ContextMenuEvent) => {
    if (!isEnabled) return;
    const contextMenuXAxisGap = 2,
      contextMenuYAxisGap = -6;

    event.preventDefault();
    event.stopPropagation();

    const editorPage = document.querySelector(`.canvas_${sectionId} .editor__page`);

    if (!editorPage) {
      return;
    }

    const clipboardData = await readBlockFromClipboard();
    if (clipboardData?.sectionId) {
      dispatch(setCanPaste(true));
    } else {
      dispatch(setCanPaste(false));
    }

    const editorRect = editorPage.getBoundingClientRect();
    dispatch(
      setLastContextMenuPosition({
        x: event.clientX,
        y: event.clientY,
        editorRect: { top: editorRect.top, left: editorRect.left, width: editorPage?.clientWidth },
      })
    );

    setContextMenu(
      contextMenu === null ? { mouseXAxis: event.clientX + contextMenuXAxisGap, mouseYAxis: event.clientY + contextMenuYAxisGap } : null
    );
  };

  const handleClose = () => {
    setContextMenu(null);
    if (containerRef.current) {
      containerRef.current.focus();
    }
  };

  const handleClick = (e: React.MouseEvent<HTMLElement>, callback: () => void) => {
    e.stopPropagation();
    e.preventDefault();

    setContextMenu(null);

    callback();
  };

  const containerRef = React.useRef<HTMLDivElement>(null);
  const anchorPosition =
    contextMenu !== null
      ? { top: contextMenu.mouseYAxis, left: contextMenu.mouseXAxis }
      : {
          top: 0,
          left: 0,
        };

  return (
    <div className="custom-context-menu" onContextMenu={handleContextMenu} data-testid={testId} ref={containerRef} tabIndex={-1}>
      {children}
      <CustomContextMenu
        anchorPosition={anchorPosition}
        options={options}
        open={contextMenu !== null && isEnabled}
        onClose={handleClose}
        handleClick={handleClick}
      />
    </div>
  );
};
